import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import type {ExtractionDiagnosticoFields} from './ExtractionDiagnosticoSection';
import {DiagnosedExtractedFieldCard} from './ExtractedFieldCard';
import {
    extractedDisplayValueIsFilled,
    extractedFieldIsFilled,
    getFirstValue,
    normalizeText,
    renderExtractedCard
} from './DocTypeExtractedViewShared';
import {formatBRL} from './extractedDisplayUtils';

type RendaDocTypeExtractedViewProps = {
    extractedData: unknown;
    diagnostico?: ExtractionDiagnosticoFields | null;
    docSubtype?: string | null;
    docSubtypeText?: string | null;
};

const RENDA_FIELDS = [
    {label: 'Nome do Titular', aliases: ['nome_titular', 'titular_nome']},
    {label: 'CPF/CNPJ', aliases: ['fonte_pagadora_cpf_cnpj', 'cpf_titular', 'cpf_cnpj', 'cpfcnpj', 'cpf', 'cnpj', 'documento_fonte_pagadora']},
    {label: 'PIS/NIS', aliases: ['pis', 'nis', 'pis_nis', 'numero_pis', 'numero_nis', 'pis_pasep_nis']},
    {label: 'Nome da Fonte Pagadora', aliases: ['fonte_pagadora_nome', 'nome_fonte_pagadora', 'fonte_pagadora', 'empregador', 'empresa']},
    {label: 'Ocupação', aliases: ['cargo_funcao', 'ocupacao', 'profissao', 'cargo']},
    {label: 'Data de Admissão', aliases: ['data_admissao', 'admission_date']},
    {
        label: 'Bruta',
        aliases: ['renda_bruta', 'salario_bruto', 'valor_bruto', 'proventos', 'bruta'],
        formatter: formatCurrencyValue
    },
    {
        label: 'Líquida',
        aliases: ['renda_liquida', 'salario_liquido', 'valor_liquido', 'liquido'],
        formatter: formatCurrencyValue
    },
    {label: 'Data do Comprovante de Renda', aliases: ['data_documento', 'data_comprovante_renda', 'data_comprovante', 'reference_date', 'competencia']}
] as const;

type LancamentoConsiderado = {
    data?: string;
    descricao?: string;
    origem?: string;
    origemPagador?: string;
    valor?: number;
    tipo?: string;
    naturezaOperacao?: string;
    considerarComoRenda?: boolean;
    motivoClassificacao?: string;
    confidence?: number;
};

type LancamentoNaoConsiderado = {
    data?: string;
    descricao?: string;
    origemPagador?: string;
    naturezaOperacao?: string;
    motivo?: string;
    valor?: number;
    confidence?: number;
};

type LancamentosNormalizados = {
    considerados: LancamentoConsiderado[];
    naoConsiderados: LancamentoNaoConsiderado[];
};

type ValoresMesItem = {
    mes?: string;
    quantidadeLancamentos?: number;
    valorTotalConsiderado?: number;
    valorTotalNaoConsiderado?: number;
};

function LancamentoCampoExibicao({label, value}: {label: string; value: string}) {
    return (
        <div>
            <p className="font-instrument text-[11px] text-primary">{label}</p>
            <p className="font-instrument text-sm text-textPrimary break-words">{value}</p>
        </div>
    );
}

type ExtratoLancamentosModalProps = {
    open: boolean;
    onClose: () => void;
    considerados: LancamentoConsiderado[];
    naoConsiderados: LancamentoNaoConsiderado[];
};

function ExtratoLancamentosModal({
    open,
    onClose,
    considerados,
    naoConsiderados
}: ExtratoLancamentosModalProps) {
    useEffect(() => {
        if (!open || typeof document === 'undefined') return undefined;

        const previousOverflow = document.body.style.overflow;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };

        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose, open]);

    if (!open || typeof document === 'undefined') return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="extrato-lancamentos-titulo"
            onClick={onClose}
        >
            <div
                className="flex max-h-[min(85vh,720px)] min-h-0 w-full max-w-5xl flex-col rounded-lg bg-card text-textPrimary shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border-subtle p-4">
                    <div className="min-w-0">
                        <h3
                            id="extrato-lancamentos-titulo"
                            className="flex items-center gap-2 font-fraunces text-lg font-semibold text-textPrimary pr-2"
                        >
                            <i className="ri-receipt-line text-xl text-primary" aria-hidden />
                            Detalhes dos lançamentos
                        </h3>
                        <p className="mt-0.5 font-instrument text-xs text-textSecondary">
                            {considerados.length} considerados · {naoConsiderados.length} não considerados
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-textSecondary hover:text-textPrimary shrink-0 p-1 rounded"
                        aria-label="Fechar"
                    >
                        <i className="ri-close-line text-xl"/>
                    </button>
                </div>
                <div className="flex-1 min-h-0 p-4 overflow-y-auto md:overflow-hidden">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:h-full md:min-h-0 md:items-stretch">
                        <div
                            className="flex min-h-0 min-w-0 flex-col rounded-md border border-border-subtle bg-muted/70 p-3 md:max-h-[min(560px,calc(85vh-7rem))]">
                            <div className="mb-2 flex-shrink-0">
                                <h4 className="font-instrument text-xs font-semibold text-textSecondary uppercase tracking-wide">
                                    Valores considerados ({considerados.length})
                                </h4>
                            </div>
                            <div className="overflow-y-auto flex-1 min-h-0 pr-1 space-y-2">
                                {considerados.length === 0 ? (
                                    <p className="font-instrument text-sm text-textSecondary italic">Nenhum lançamento.</p>
                                ) : (
                                    considerados.map((item, idx) => (
                                        <div
                                            key={`vc-${idx}`}
                                            className="space-y-1.5 rounded-md border border-border-subtle bg-card p-3"
                                        >
                                            <p className="font-instrument text-sm font-semibold text-textPrimary">
                                                Lançamento {idx + 1}
                                            </p>
                                            <LancamentoCampoExibicao label="Data" value={item.data ?? '—'}/>
                                            <LancamentoCampoExibicao label="Descrição" value={item.descricao ?? '—'}/>
                                            <LancamentoCampoExibicao
                                                label="Natureza da operação"
                                                value={item.naturezaOperacao ?? item.tipo ?? '—'}
                                            />
                                            <LancamentoCampoExibicao
                                                label="Origem/Pagador"
                                                value={item.origemPagador ?? item.origem ?? '—'}
                                            />
                                            <LancamentoCampoExibicao label="Valor" value={formatBRL(item.valor)}/>
                                            <LancamentoCampoExibicao label="Confiança" value={formatConfidence(item.confidence)}/>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <div
                            className="flex min-h-0 min-w-0 flex-col rounded-md border border-border-subtle bg-muted/70 p-3 md:max-h-[min(560px,calc(85vh-7rem))]">
                            <div className="mb-2 flex-shrink-0">
                                <h4 className="font-instrument text-xs font-semibold text-textSecondary uppercase tracking-wide">
                                    Valores não considerados ({naoConsiderados.length})
                                </h4>
                            </div>
                            <div className="overflow-y-auto flex-1 min-h-0 pr-1 space-y-2">
                                {naoConsiderados.length === 0 ? (
                                    <p className="font-instrument text-sm text-textSecondary italic">Nenhum lançamento.</p>
                                ) : (
                                    naoConsiderados.map((item, idx) => (
                                        <div
                                            key={`vnc-${idx}`}
                                            className="space-y-1.5 rounded-md border border-border-subtle bg-card p-3"
                                        >
                                            <p className="font-instrument text-sm font-semibold text-textPrimary">
                                                Item {idx + 1}
                                            </p>
                                            <LancamentoCampoExibicao label="Data" value={item.data ?? '—'}/>
                                            <LancamentoCampoExibicao label="Descrição" value={item.descricao ?? '—'}/>
                                            <LancamentoCampoExibicao
                                                label="Natureza da operação"
                                                value={item.naturezaOperacao ?? '—'}
                                            />
                                            <LancamentoCampoExibicao
                                                label="Origem/Pagador"
                                                value={item.origemPagador ?? '—'}
                                            />
                                            <LancamentoCampoExibicao label="Motivo" value={item.motivo ?? '—'}/>
                                            <LancamentoCampoExibicao label="Valor" value={formatBRL(item.valor)}/>
                                            <LancamentoCampoExibicao label="Confiança" value={formatConfidence(item.confidence)}/>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

function asObject(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
}

function normalizeFieldKey(key: string): string {
    return key
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
}

function collectArrayValuesByAliases(source: unknown, aliases: readonly string[]): unknown[] {
    const normalizedAliases = new Set(aliases.map(normalizeFieldKey));
    const visited = new WeakSet<object>();
    const values: unknown[] = [];

    function visit(value: unknown): void {
        if (!value || typeof value !== 'object') return;
        if (visited.has(value)) return;
        visited.add(value);

        if (Array.isArray(value)) {
            value.forEach(visit);
            return;
        }

        Object.entries(value as Record<string, unknown>).forEach(([key, nestedValue]) => {
            if (normalizedAliases.has(normalizeFieldKey(key)) && Array.isArray(nestedValue)) {
                values.push(...nestedValue);
            }
            visit(nestedValue);
        });
    }

    visit(source);
    return values;
}

function normalizeBoolean(value: unknown): boolean | undefined {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true' || normalized === 'sim' || normalized === '1') return true;
        if (normalized === 'false' || normalized === 'nao' || normalized === 'não' || normalized === '0') return false;
    }
    return undefined;
}

function normalizeNumber(value: unknown): number | undefined {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string') {
        const raw = value.trim();
        if (!raw) return undefined;
        const normalized = raw
            .replace(/[R$\s]/g, '')
            .replace(/\.(?=\d{3}(?:\D|$))/g, '')
            .replace(',', '.')
            .replace(/[^\d.-]/g, '');
        const parsed = Number(normalized);
        if (!Number.isNaN(parsed)) return parsed;
    }
    return undefined;
}

function getNumberValue(source: unknown, aliases: readonly string[]): number | undefined {
    return normalizeNumber(getFirstValue(source, aliases));
}

function formatCurrencyValue(value: unknown): string {
    const parsed = normalizeNumber(value);
    if (parsed == null) return normalizeText(value) ?? '—';
    return formatBRL(parsed);
}

function formatConfidence(value: number | undefined): string {
    if (value == null || Number.isNaN(value)) return '—';
    const pct = value >= 0 && value <= 1 ? value * 100 : value;
    return `${pct.toFixed(1)}%`;
}

function parseLancamento(item: Record<string, unknown>): LancamentoConsiderado {
    return {
        data: normalizeText(getFirstValue(item, ['data', 'date'])) ?? undefined,
        descricao: normalizeText(getFirstValue(item, ['descricao', 'description', 'origem'])) ?? undefined,
        origem: normalizeText(getFirstValue(item, ['origem', 'source'])) ?? undefined,
        origemPagador: normalizeText(getFirstValue(item, ['origem_pagador', 'origemPagador', 'payer', 'origem'])) ?? undefined,
        valor: getNumberValue(item, ['valor', 'value', 'amount']),
        tipo: normalizeText(getFirstValue(item, ['tipo', 'type'])) ?? undefined,
        naturezaOperacao: normalizeText(getFirstValue(item, ['natureza_operacao', 'naturezaOperacao', 'tipo', 'type'])) ?? undefined,
        considerarComoRenda: normalizeBoolean(getFirstValue(item, ['considerar_como_renda', 'considerarComoRenda'])),
        motivoClassificacao: normalizeText(getFirstValue(item, ['motivo_classificacao', 'motivoClassificacao', 'motivo', 'reason'])) ?? undefined,
        confidence: getNumberValue(item, ['confidence', 'confianca'])
    };
}

function isNovoFormatoLancamento(item: Record<string, unknown>): boolean {
    return (
        Object.prototype.hasOwnProperty.call(item, 'considerar_como_renda') ||
        Object.prototype.hasOwnProperty.call(item, 'considerarComoRenda')
    );
}

function lancamentoKey(item: LancamentoConsiderado | LancamentoNaoConsiderado): string {
    return [
        item.data,
        item.descricao,
        item.origemPagador,
        item.naturezaOperacao,
        item.valor,
    ].map((value) => String(value ?? '').trim().toLowerCase()).join('|');
}

function toLancamentoNaoConsiderado(
    parsed: LancamentoConsiderado,
    motivoFallback?: string
): LancamentoNaoConsiderado {
    return {
        data: parsed.data,
        descricao: parsed.descricao,
        origemPagador: parsed.origemPagador,
        naturezaOperacao: parsed.naturezaOperacao,
        motivo: parsed.motivoClassificacao ?? motivoFallback,
        valor: parsed.valor,
        confidence: parsed.confidence
    };
}

function addLancamentoConsiderado(
    target: LancamentoConsiderado[],
    seen: Set<string>,
    item: LancamentoConsiderado
): void {
    const key = lancamentoKey(item);
    if (seen.has(key)) return;
    seen.add(key);
    target.push(item);
}

function addLancamentoNaoConsiderado(
    target: LancamentoNaoConsiderado[],
    seen: Set<string>,
    item: LancamentoNaoConsiderado
): void {
    const key = lancamentoKey(item);
    if (seen.has(key)) return;
    seen.add(key);
    target.push(item);
}

function normalizeExtratoLancamentos(extractedData: unknown): LancamentosNormalizados {
    const consideradosRaw = collectArrayValuesByAliases(extractedData, [
        'valor_considerado',
        'considered_income'
    ]);
    const naoConsideradosRaw = collectArrayValuesByAliases(extractedData, [
        'valor_nao_considerado',
        'not_considered_income'
    ]);
    const creditosIdentificadosRaw = collectArrayValuesByAliases(extractedData, ['creditos_identificados']);

    const considerados: LancamentoConsiderado[] = [];
    const naoConsiderados: LancamentoNaoConsiderado[] = [];
    const seenConsiderados = new Set<string>();
    const seenNaoConsiderados = new Set<string>();

    consideradosRaw
        .map((item) => asObject(item))
        .filter((item): item is Record<string, unknown> => Boolean(item))
        .map((item) => parseLancamento(item))
        .forEach((item) => addLancamentoConsiderado(considerados, seenConsiderados, item));

    naoConsideradosRaw
        .map((item) => asObject(item))
        .filter((item): item is Record<string, unknown> => Boolean(item))
        .map((item) => {
            const parsed = parseLancamento(item);
            return toLancamentoNaoConsiderado(parsed);
        })
        .forEach((item) => addLancamentoNaoConsiderado(naoConsiderados, seenNaoConsiderados, item));

    if (creditosIdentificadosRaw.length > 0 && (considerados.length === 0 || naoConsiderados.length === 0)) {
        const parsedCreditos = creditosIdentificadosRaw
            .map((item) => asObject(item))
            .filter((item): item is Record<string, unknown> => Boolean(item))
            .map((item) => ({raw: item, parsed: parseLancamento(item)}));

        const hasNovoFormato = parsedCreditos.some((item) => isNovoFormatoLancamento(item.raw));
        if (hasNovoFormato) {
            parsedCreditos.forEach(({parsed}) => {
                if (parsed.considerarComoRenda === true && considerados.length === 0) {
                    addLancamentoConsiderado(considerados, seenConsiderados, parsed);
                } else if (parsed.considerarComoRenda === false && naoConsiderados.length === 0) {
                    addLancamentoNaoConsiderado(
                        naoConsiderados,
                        seenNaoConsiderados,
                        toLancamentoNaoConsiderado(parsed)
                    );
                }
            });
        }
    }

    return {
        considerados,
        naoConsiderados
    };
}

function normalizeValoresMes(extractedData: unknown): ValoresMesItem[] {
    const valoresMesRaw = getFirstValue(extractedData, [
        'valores_mes',
        'monthly_incomes',
    ]);
    if (!Array.isArray(valoresMesRaw)) return [];

    return valoresMesRaw
        .map((item) => asObject(item))
        .filter((item): item is Record<string, unknown> => Boolean(item))
        .map((item) => ({
            mes: normalizeText(getFirstValue(item, ['mes', 'month', 'periodo_referencia', 'reference_period'])) ?? undefined,
            quantidadeLancamentos: getNumberValue(item, ['quantidade_lancamentos', 'number_transactions', 'transactions_count']),
            valorTotalConsiderado: getNumberValue(item, [
                'valor_total_considerado',
                'considered_total_income',
                'total_considerado'
            ]),
            valorTotalNaoConsiderado: getNumberValue(item, [
                'valor_total_nao_considerado',
                'not_considered_total_income',
                'total_nao_considerado'
            ])
        }));
}

function sumDefinedValues<T>(items: T[], selector: (item: T) => number | undefined): number | undefined {
    let hasValue = false;
    const total = items.reduce((acc, item) => {
        const value = selector(item);
        if (value == null || Number.isNaN(value)) return acc;
        hasValue = true;
        return acc + value;
    }, 0);
    return hasValue ? total : undefined;
}

function resolveExtratoTotalConsiderado(
    extractedData: unknown,
    valoresMes: ValoresMesItem[],
    considerados: LancamentoConsiderado[]
): number | undefined {
    return (
        getNumberValue(extractedData, [
            'valor_total_considerado',
            'total_considerado',
            'considered_total_income',
            'valorTotalConsiderado'
        ]) ??
        sumDefinedValues(valoresMes, (item) => item.valorTotalConsiderado) ??
        sumDefinedValues(considerados, (item) => item.valor)
    );
}

function resolveExtratoTotalNaoConsiderado(
    extractedData: unknown,
    valoresMes: ValoresMesItem[],
    naoConsiderados: LancamentoNaoConsiderado[]
): number | undefined {
    return (
        getNumberValue(extractedData, [
            'valor_total_nao_considerado',
            'total_nao_considerado',
            'not_considered_total_income',
            'valorTotalNaoConsiderado'
        ]) ??
        sumDefinedValues(valoresMes, (item) => item.valorTotalNaoConsiderado) ??
        sumDefinedValues(naoConsiderados, (item) => item.valor)
    );
}

function RendaResumoConsolidado({
    valoresMes,
    totalConsiderado,
    totalNaoConsiderado
}: {
    valoresMes: ValoresMesItem[];
    totalConsiderado?: number;
    totalNaoConsiderado?: number;
}) {
    const items: ValoresMesItem[] = valoresMes.length > 0
        ? valoresMes
        : [{valorTotalConsiderado: totalConsiderado, valorTotalNaoConsiderado: totalNaoConsiderado}];

    return (
        <div className="space-y-2">
            {items.map((item, index) => (
                <div
                    key={`${item.mes ?? 'consolidado'}-${index}`}
                    className="rounded-md border border-border-subtle bg-card p-2.5"
                >
                    <div className="flex items-center justify-between gap-3">
                        <p className="font-instrument text-sm font-semibold text-textPrimary">
                            {item.mes ?? 'Resumo consolidado da renda'}
                        </p>
                        {item.quantidadeLancamentos != null ? (
                            <span className="shrink-0 font-instrument text-[11px] text-textSecondary">
                                {item.quantidadeLancamentos} lançamento{item.quantidadeLancamentos !== 1 ? 's' : ''}
                            </span>
                        ) : null}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 font-instrument text-sm text-textPrimary">
                        <span>
                            Considerado: <span className="font-semibold">{formatBRL(item.valorTotalConsiderado)}</span>
                        </span>
                        <span>
                            Não considerado: <span className="font-semibold">{formatBRL(item.valorTotalNaoConsiderado)}</span>
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );
}

function isExtratoBancarioSubtype(docSubtype: string | null | undefined): boolean {
    return (docSubtype ?? '').trim().toUpperCase().replace(/\s+/g, '_') === 'EXTRATO_BANCARIO';
}

export function RendaDocTypeExtractedView({
    extractedData,
    diagnostico,
    docSubtype
}: RendaDocTypeExtractedViewProps) {
    const [lancamentosOpen, setLancamentosOpen] = useState(false);
    const isExtratoBancario = isExtratoBancarioSubtype(docSubtype);
    const {considerados, naoConsiderados} = normalizeExtratoLancamentos(extractedData);
    const valoresMes = normalizeValoresMes(extractedData);
    const periodoReferenciaExtrato = normalizeText(getFirstValue(extractedData, ['periodo_referencia', 'periodo_ref']));
    const temLancamentos = considerados.length > 0 || naoConsiderados.length > 0;
    const totalConsiderado = resolveExtratoTotalConsiderado(extractedData, valoresMes, considerados);
    const totalNaoConsiderado = resolveExtratoTotalNaoConsiderado(extractedData, valoresMes, naoConsiderados);
    const hasResumoMensal = valoresMes.length > 0 || totalConsiderado != null || totalNaoConsiderado != null;
    const shouldShowPeriodoReferencia = isExtratoBancario || temLancamentos;
    const fieldCards = [
        ...RENDA_FIELDS.map((field, index) => ({
            filled: extractedFieldIsFilled(field, extractedData),
            index,
            node: renderExtractedCard(field, extractedData, diagnostico)
        })),
        ...(shouldShowPeriodoReferencia ? [{
            filled: extractedDisplayValueIsFilled(periodoReferenciaExtrato),
            index: RENDA_FIELDS.length,
            node: (
                <DiagnosedExtractedFieldCard
                    key="Período de Referência"
                    label="Período de Referência"
                    displayValue={periodoReferenciaExtrato ?? '—'}
                    copyText={periodoReferenciaExtrato ?? null}
                    diagnostico={diagnostico}
                    scoreFieldKey={['periodo_referencia', 'periodo_ref']}
                />
            )
        }] : [])
    ].sort((left, right) => Number(right.filled) - Number(left.filled) || left.index - right.index);

    return (
        <div className="space-y-2">
            {hasResumoMensal ? (
                <RendaResumoConsolidado
                    valoresMes={valoresMes}
                    totalConsiderado={totalConsiderado}
                    totalNaoConsiderado={totalNaoConsiderado}
                />
            ) : null}

            {temLancamentos ? (
                <div className="rounded-md border border-border-subtle bg-card p-2.5">
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5 font-instrument text-[10px] font-semibold uppercase tracking-wide text-textSecondary">
                            <i className="ri-receipt-line text-sm" aria-hidden />
                            Lançamentos do Extrato
                        </div>
                        <p className="mt-0.5 font-instrument text-sm text-textPrimary">
                            <span className="font-semibold">{considerados.length}</span> considerado{considerados.length !== 1 ? 's' : ''} e{' '}
                            <span className="font-semibold">{naoConsiderados.length}</span> não considerado{naoConsiderados.length !== 1 ? 's' : ''}.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setLancamentosOpen(true)}
                        className="mt-2 flex min-h-10 w-full items-center justify-between gap-2 rounded-md border border-amber-300/60 bg-amber-500/10 px-2.5 py-2 text-left font-instrument text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-500/15 dark:text-amber-200"
                    >
                        <span className="flex min-w-0 items-center gap-2">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-500/15">
                                <i className="ri-list-check-3 text-base" aria-hidden />
                            </span>
                            <span className="whitespace-normal leading-tight">Ver lançamentos detalhados</span>
                        </span>
                        <i className="ri-arrow-right-s-line shrink-0 text-lg" aria-hidden />
                    </button>
                </div>
            ) : null}

            {fieldCards.map((item) => item.node)}
            <ExtratoLancamentosModal
                open={lancamentosOpen}
                onClose={() => setLancamentosOpen(false)}
                considerados={considerados}
                naoConsiderados={naoConsiderados}
            />
        </div>
    );
}
